import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Typography,
  IconButton,
} from "@mui/material";
import { ExpandMore, AddCircleOutline } from "@mui/icons-material";

export function CategoryCard({ children }: { children: React.ReactNode }) {
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        padding: 2,
        rowGap: 3,
      }}
    >
      {children}
    </Card>
  );
}

type SpecialHeaderBehavior =
  | "initialRenderAsUnstarted"
  | "renderHeaderAccordion";

interface QuestionContainerProps {
  children: React.ReactNode;
  title?: string;
  unstartedSubtitle?: string;
  specialHeaderBehavior?: SpecialHeaderBehavior;
  unstarted?: boolean;
}

export function CategoryContainer({
  title,
  children,
  specialHeaderBehavior,
  unstartedSubtitle,
  unstarted = true,
}: QuestionContainerProps) {
  const titleJsx = title && (
    <Typography sx={{ fontWeight: "600" }}>{title}</Typography>
  );

  const [questionStarted, setQuestionStarted] = React.useState(!unstarted);
  const [accordionExpanded, setAccordionExpanded] = React.useState(true);

  // We want to render the accordion style header for CategoryContainer in two situations:
  // 1. If specialHeaderBehavior prop has been set to renderHeaderAccordion
  // 2. If specialHeaderBehavior prop was set to initialRenderAsUnstarted, and the user "started" the question by clicking the green (+)
  if (
    specialHeaderBehavior === "renderHeaderAccordion" ||
    (specialHeaderBehavior === "initialRenderAsUnstarted" && questionStarted)
  ) {
    return (
      <CategoryCard>
        <Accordion
          expanded={accordionExpanded}
          onChange={() => setAccordionExpanded(!accordionExpanded)}
          disableGutters={true}
          sx={{
            boxShadow: "none",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            sx={{
              paddingX: 0,
              paddingBottom: 2,
            }}
          >
            {titleJsx}
          </AccordionSummary>
          <AccordionDetails
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: 0,
              rowGap: 3,
            }}
          >
            {children}
          </AccordionDetails>
        </Accordion>
      </CategoryCard>
    );
  }

  if (specialHeaderBehavior === "initialRenderAsUnstarted") {
    return (
      <CategoryCard>
        {titleJsx}
        <Box
          sx={{
            position: "relative",
          }}
        >
          {unstartedSubtitle}
          <IconButton
            onClick={() => setQuestionStarted(true)}
            sx={{
              position: "absolute",
              right: "0.33%",
              bottom: "34.33%",
              cursor: "pointer",
            }}
          >
            <AddCircleOutline fontSize="large" color="primary" />
          </IconButton>
        </Box>
      </CategoryCard>
    );
  }

  return (
    <CategoryCard>
      {titleJsx}
      {children}
    </CategoryCard>
  );
}
